/**
 * Onboarding Documents — IN-2.c
 * ─────────────────────────────
 * Wizard-side validation + input shaping for compliance document
 * uploads. The actual ledger write goes through the existing IN-1.b
 * writer `submitComplianceDocument` via the OnboardingProvider.
 *
 * Hard rules (IN-2.c):
 *   • Accept only the locked MIME allowlist (pdf/jpg/png/heic).
 *   • Reject files > 10MB.
 *   • Reject any base64 / data: URL — wizard NEVER persists binary
 *     payload to localStorage. Only metadata is stored.
 *   • Document category must be one of the compliance types derived
 *     from the onboarding compliance resolver. Free-form `"other"` is blocked.
 */

import {
  ALLOWED_DOCUMENT_MIME_TYPES,
  MAX_DOCUMENT_UPLOAD_BYTES,
  onboardingContractorId,
  type OnboardingSession,
} from "./types";
import type {
  ComplianceDocument,
  ComplianceDocumentType,
  SubmitComplianceDocumentInput,
} from "@/data/contractor/compliance/complianceDocuments";
import {
  getRequiredOnboardingCompliance,
  type ComplianceRequirements,
} from "@/data/contractor/compliance/complianceRequirements";

export type OnboardingDocumentType = Extract<
  ComplianceDocumentType,
  "license" | "insurance" | "w9" | "background_check" | "certification"
>;

const ALLOWED_DOC_CATEGORIES: ReadonlySet<OnboardingDocumentType> = new Set([
  "license",
  "insurance",
  "w9",
  "background_check",
  "certification",
]);

export interface OnboardingDocumentUploadInput {
  type: OnboardingDocumentType;
  fileName: string;
  fileSize: number;
  mimeType: string;
  /** Optional placeholder blob handle — never persisted. */
  blobRefId?: string;
}

/** Pure validation. Throws with a user-safe message on rejection. */
export function validateOnboardingDocumentUpload(
  input: OnboardingDocumentUploadInput,
): void {
  if (!input || typeof input !== "object") {
    throw new Error("onboardingDocuments: input is required.");
  }
  if (!ALLOWED_DOC_CATEGORIES.has(input.type)) {
    throw new Error(
      `onboardingDocuments: document type "${input.type}" is not a compliance category.`,
    );
  }
  const fileName = (input.fileName ?? "").trim();
  if (!fileName) {
    throw new Error("onboardingDocuments: fileName is required.");
  }
  if (
    !ALLOWED_DOCUMENT_MIME_TYPES.includes(
      input.mimeType as (typeof ALLOWED_DOCUMENT_MIME_TYPES)[number],
    )
  ) {
    throw new Error(
      `onboardingDocuments: file type "${input.mimeType}" is not allowed. Accepted: PDF, JPG, PNG, HEIC.`,
    );
  }
  if (typeof input.fileSize !== "number" || input.fileSize <= 0) {
    throw new Error("onboardingDocuments: fileSize must be a positive number.");
  }
  if (input.fileSize > MAX_DOCUMENT_UPLOAD_BYTES) {
    throw new Error(
      `onboardingDocuments: file exceeds 10MB limit (${input.fileSize} bytes).`,
    );
  }
  // Forbid base64 / data: URL leakage via blobRefId.
  if (input.blobRefId && /^data:|;base64,/i.test(input.blobRefId)) {
    throw new Error(
      "onboardingDocuments: base64 / data URLs are not accepted — metadata only.",
    );
  }
}

/**
 * Build the metadata-only input for the IN-1.b ledger writer. Returns
 * a `SubmitComplianceDocumentInput` with NO binary payload — `url` is
 * deliberately omitted; `metadata` carries fileSize / mimeType /
 * sessionId so the bridge to a real contractor row (IN-2.g) can match.
 */
export function buildLedgerInputForOnboarding(
  session: Pick<OnboardingSession, "id">,
  input: OnboardingDocumentUploadInput,
): SubmitComplianceDocumentInput {
  validateOnboardingDocumentUpload(input);
  return {
    contractorId: onboardingContractorId(session.id),
    type: input.type,
    fileName: input.fileName.trim(),
    metadata: {
      fileSize: input.fileSize,
      mimeType: input.mimeType,
      sessionId: session.id,
      blobRefId: input.blobRefId,
    },
  };
}

/** Derive which document categories the session must upload. */
export function getRequiredOnboardingDocumentTypes(
  session: Pick<OnboardingSession, "entityType" | "parentEntityId" | "draft">,
  allSessions: Array<
    Pick<OnboardingSession, "contractorId" | "entityType" | "draft">
  > = [],
): OnboardingDocumentType[] {
  const reqs: ComplianceRequirements = getRequiredOnboardingCompliance(
    session,
    allSessions,
  );
  const out: OnboardingDocumentType[] = [];
  if (reqs.licenseRequired && !reqs.inheritsFromParent.license) out.push("license");
  if (reqs.insuranceRequired && !reqs.inheritsFromParent.insurance)
    out.push("insurance");
  if (reqs.w9Required && !reqs.inheritsFromParent.w9) out.push("w9");
  if (reqs.backgroundCheckRequired) out.push("background_check");
  if (reqs.agreementVariant === "certification_agreement") {
    out.push("certification");
  }
  return out;
}

function getOnboardingDocumentContractorIds(
  session: Pick<OnboardingSession, "id" | "contractorId">,
): Set<string> {
  const ids = new Set<string>([onboardingContractorId(session.id)]);
  const realContractorId = String(session.contractorId ?? "").trim();
  if (realContractorId) ids.add(realContractorId);
  return ids;
}

/** Active documents for a session (not rejected, not superseded). */
export function getOnboardingDocumentsForSession(
  ledger: ComplianceDocument[],
  session: Pick<OnboardingSession, "id" | "contractorId">,
): ComplianceDocument[] {
  const contractorIds = getOnboardingDocumentContractorIds(session);
  return ledger.filter(
    (d) => contractorIds.has(d.contractorId) && !d.supersededBy && !d.rejectedAt,
  );
}

/** All required types have at least one active uploaded doc. */
export function areRequiredDocumentsUploaded(
  ledger: ComplianceDocument[],
  session: Pick<
    OnboardingSession,
    "id" | "contractorId" | "entityType" | "parentEntityId" | "draft"
  >,
  allSessions: Array<
    Pick<OnboardingSession, "contractorId" | "entityType" | "draft">
  > = [],
): boolean {
  const required = getRequiredOnboardingDocumentTypes(session, allSessions);
  if (required.length === 0) return true;
  const active = getOnboardingDocumentsForSession(ledger, session);
  return required.every((t) => active.some((d) => d.type === t));
}

export { onboardingContractorId };
